import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { useShape } from "@teamna/web-client/hooks/use-shape.ts";
import {
  useAddFamilies,
  useAddFamilyAdult,
  useAddFamilyChild,
  useCheckFamilyLink,
  useFamilyHold,
  useInviteFamilies,
  useLinkFamilies,
  useRemoveFamily,
  useRemoveFamilyAdult,
  useRemoveFamilyChild,
  useRestoreFamily,
  useRestoreFamilyChild,
  useUndoFamilies,
  useUndoFamilyLink,
  useUpdateFamilyAdult,
  useUpdateFamilyChild,
} from "@teamna/web-client/hooks/use-families.ts";
import { usePlaceParticipant } from "@teamna/web-client/hooks/use-setup.ts";
import type { Words } from "./model.ts";
import type { FamilyActions } from "./types.ts";

/**
 * Every family write, wired to the API once. The Parents list, a parent's page and the
 * participants page all edit the same families, so they share this.
 */
export function useFamilyActions(businessId: string): FamilyActions {
  const addFamilies = useAddFamilies(businessId);
  const undoFamilies = useUndoFamilies(businessId);
  const updateAdult = useUpdateFamilyAdult(businessId);
  const addAdult = useAddFamilyAdult(businessId);
  const removeAdult = useRemoveFamilyAdult(businessId);
  const addChild = useAddFamilyChild(businessId);
  const updateChild = useUpdateFamilyChild(businessId);
  const removeChild = useRemoveFamilyChild(businessId);
  const restoreChild = useRestoreFamilyChild(businessId);
  const hold = useFamilyHold(businessId);
  const removeFamily = useRemoveFamily(businessId);
  const restoreFamily = useRestoreFamily(businessId);
  const place = usePlaceParticipant(businessId);
  const invite = useInviteFamilies(businessId);
  const checkLink = useCheckFamilyLink(businessId);
  const linkFamilies = useLinkFamilies(businessId);
  const undoLink = useUndoFamilyLink(businessId);

  return {
    addFamilies: (input) => addFamilies.mutateAsync(input),
    undoFamilies: async (batch) => {
      await undoFamilies.mutateAsync(batch);
    },
    updateAdult: async (familyId, adultId, patch) => {
      await updateAdult.mutateAsync({ familyId, adultId, ...patch });
    },
    addAdult: async (familyId, adult) => {
      await addAdult.mutateAsync({ familyId, ...adult });
    },
    removeAdult: async (familyId, adultId) => {
      await removeAdult.mutateAsync({ familyId, adultId });
    },
    addChild: async (familyId, child) => {
      await addChild.mutateAsync({ familyId, name: child.name, age: child.age });
    },
    updateChild: async (familyId, participantId, patch) => {
      await updateChild.mutateAsync({ familyId, participantId, ...patch });
    },
    removeChild: async (familyId, participantId) => {
      await removeChild.mutateAsync({ familyId, participantId });
    },
    restoreChild: async (familyId, participantId) => {
      await restoreChild.mutateAsync({ familyId, participantId });
    },
    setHold: async (familyId, note) => {
      await hold.mutateAsync({ familyId, note });
    },
    removeFamily: (familyId) => removeFamily.mutateAsync({ familyId }),
    restoreFamily: async (removed) => {
      await restoreFamily.mutateAsync(removed);
    },
    place: async (participantId, target) => {
      await place.mutateAsync(
        target === "unsure"
          ? { id: participantId, groupId: null, placementStatus: "unsure" }
          : target
            ? { id: participantId, groupId: target, confirm: true }
            : { id: participantId, groupId: null, placementStatus: "unplaced" },
      );
    },
    invite: (familyIds) => invite.mutateAsync({ familyIds }),
    checkLink: (familyIds) => checkLink.mutateAsync(familyIds),
    linkFamilies: (keepFamilyId, mergeFamilyId) => linkFamilies.mutateAsync({ keepFamilyId, mergeFamilyId }),
    undoLink: async (linkId) => {
      await undoLink.mutateAsync(linkId);
    },
  };
}

/** The business's words, lower case, and who books (the shape), as the family views and Home read them. */
export function useFamilyWords(): Words {
  const { labels } = useLabels();
  const { forChildren } = useShape();
  return {
    forChildren,
    group: labels.groupSingular.toLowerCase(),
    groups: labels.groupPlural.toLowerCase(),
    participant: labels.participantSingular.toLowerCase(),
    participants: labels.participantPlural.toLowerCase(),
    staff: labels.staffSingular.toLowerCase(),
    member: labels.memberSingular.toLowerCase(),
    members: labels.memberPlural.toLowerCase(),
  };
}
